import {
  CreateLocalTrackOptions as CreateTwilioLocalTrackOptions,
  LocalParticipant as TwilioLocalParticipant,
} from "twilio-video";
import { AudioVideoMediaKind } from "../../core/room/participant/media/types";
import { AudioVideoSettings, LocalAVTrackContextAtom } from "../../core/types";
import { getStoredAudioVideoSettings } from "../../core/utils";
import { gracefullyCreateLocalTrack } from "./gracefullyCreateLocalTrack";
import { TrackOperationResult } from "./types";
import { unpublishLocalAudioVideoTrack } from "./unpublishLocalAudioVideoTrack";

/**
 * Returns the existing local media track or creates a new one. If a device ID is
 * specified, then the existing track is unpublished and a new one is created.
 */
export async function getOrCreateLocalMediaTrack(
  trackKind: AudioVideoMediaKind,
  getLocalTrackState: () => LocalAVTrackContextAtom,
  setStoredAudioVideoSettings: (
    updater: (prev: AudioVideoSettings) => AudioVideoSettings,
  ) => void,
  localParticipant?: TwilioLocalParticipant,
  trackOptions?: CreateTwilioLocalTrackOptions,
): Promise<TrackOperationResult> {
  const targetDeviceId = trackOptions?.deviceId;
  const storedAudioVideoSettings = getStoredAudioVideoSettings();
  const initialLocalTrackState = getLocalTrackState();

  // If no device switching is requested and we have an existing track, return it
  if (!targetDeviceId && initialLocalTrackState.track) {
    return {
      success: true,
      ...initialLocalTrackState,
    };
  }

  // If device switching is requested, unpublish existing track first
  let currentLocalTrackState = initialLocalTrackState;
  // If there's no existing track, skip unpublishing
  if (targetDeviceId && initialLocalTrackState.track) {
    const unpublishTrackResult = await unpublishLocalAudioVideoTrack(
      trackKind,
      initialLocalTrackState,
      true,
      localParticipant,
    );

    if (!unpublishTrackResult.success) return unpublishTrackResult;

    // Use the result from unpublish operation (mutex ensures this is up-to-date)
    currentLocalTrackState = {
      track: unpublishTrackResult.track,
      status: unpublishTrackResult.status,
    };
  }

  // Create new track
  const deviceId =
    trackKind === "audio"
      ? (targetDeviceId ?? storedAudioVideoSettings?.inputAudioDeviceId)
      : (targetDeviceId ?? storedAudioVideoSettings?.inputVideoDeviceId);

  return await gracefullyCreateLocalTrack(
    trackKind,
    {
      ...trackOptions,
      deviceId,
    },
    currentLocalTrackState,
    setStoredAudioVideoSettings,
  );
}
